/**
 * Power Selection - Choose superpowers for your creature
 * 16 cards: 8 biotic (living) + 8 abiotic (non-living)
 * Ages 4-6, Large touch targets, Multi-select
 */

import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SuperpowerCard, PowerType } from './types';
import { BIOTIC_POWERS, ABIOTIC_POWERS, ALL_POWERS } from './data';

// Shuffle function for randomizing cards
function shuffleArray<T>(array: T[]): T[] {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
}

interface PowerSelectionProps {
  language: 'en' | 'es';
  onComplete: (powers: SuperpowerCard[]) => void;
}

const TEXT = {
  en: {
    title: 'Choose Superpowers!',
    subtitle: 'Pick up to 3 powers from nature to give your creature',
    biotic: 'Living Things',
    abiotic: 'Nature Forces',
    all: 'All Powers',
    selected: 'of 3 selected',
    continue: 'Continue',
    selectOne: 'Choose at least 1 power (max 3)',
    bioticDesc: 'Powers from plants and animals',
    abioticDesc: 'Powers from sun, water, and earth',
    maxReached: 'Max powers reached! Tap to deselect.'
  },
  es: {
    title: '¡Elige Superpoderes!',
    subtitle: 'Elige hasta 3 poderes de la naturaleza para tu criatura',
    biotic: 'Seres Vivos',
    abiotic: 'Fuerzas Naturales',
    all: 'Todos los Poderes',
    selected: 'de 3 seleccionados',
    continue: 'Continuar',
    selectOne: 'Elige al menos 1 poder (máximo 3)',
    bioticDesc: 'Poderes de plantas y animales',
    abioticDesc: 'Poderes del sol, agua y tierra',
    maxReached: '¡Máximo alcanzado! Toca para deseleccionar.'
  }
};

type FilterType = 'all' | 'biotic' | 'abiotic';

export default function PowerSelection({ language, onComplete }: PowerSelectionProps) {
  const [selectedPowers, setSelectedPowers] = useState<SuperpowerCard[]>([]);
  const [filter, setFilter] = useState<FilterType>('all');

  const t = TEXT[language];

  // Get filtered powers and shuffle them randomly
  const filteredPowers = useMemo(() => {
    let powers: SuperpowerCard[];
    switch (filter) {
      case 'biotic':
        powers = BIOTIC_POWERS;
        break;
      case 'abiotic':
        powers = ABIOTIC_POWERS;
        break;
      default:
        powers = ALL_POWERS;
    }
    return shuffleArray(powers);
  }, [filter]);

  // Toggle power selection (max 3 cards)
  const MAX_POWERS = 3;

  const togglePower = (power: SuperpowerCard) => {
    setSelectedPowers(prev => {
      const isSelected = prev.some(p => p.id === power.id);
      if (isSelected) {
        // Deselect
        return prev.filter(p => p.id !== power.id);
      } else {
        // Select (max 7)
        if (prev.length >= MAX_POWERS) {
          return prev; // Don't allow more than 7
        }
        const newSelection = [...prev, power];

        // Auto-advance when 7 cards are selected
        if (newSelection.length === MAX_POWERS) {
          setTimeout(() => {
            onComplete(newSelection);
          }, 600); // Small delay to show the selection animation
        }

        return newSelection;
      }
    });
  };

  const isPowerSelected = (powerId: string) => {
    return selectedPowers.some(p => p.id === powerId);
  };

  const handleContinue = () => {
    if (selectedPowers.length > 0) {
      onComplete(selectedPowers);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] flex flex-col items-center p-6">
      <div className="max-w-6xl w-full">
        {/* Header */}
        <motion.div
          initial={{ y: -30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center mb-6"
        >
          <h2
            className="text-4xl md:text-6xl mb-2"
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontWeight: 300,
              color: '#2a6d5b',
              letterSpacing: '-0.015em',
            }}
          >
            {t.title}
          </h2>
          <p
            className="text-xl md:text-2xl mb-4"
            style={{ fontFamily: "'Inter', system-ui, sans-serif", color: '#355c46' }}
          >
            {t.subtitle}
          </p>

          {/* Selection counter — neutral cream chip; brand green when full */}
          <div
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full transition-all"
            style={{
              background: selectedPowers.length === MAX_POWERS ? '#42a57a' : '#f4efde',
              color: selectedPowers.length === MAX_POWERS ? '#ffffff' : '#2a6d5b',
              fontFamily: "'Inter', system-ui, sans-serif",
            }}
          >
            <span style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 30, fontWeight: 600 }}>
              {selectedPowers.length}
            </span>
            <span style={{ fontSize: 18, fontWeight: 500 }}>
              {t.selected}
            </span>
          </div>

          {/* Max Reached Message */}
          {selectedPowers.length === MAX_POWERS && (
            <motion.p
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="mt-2"
              style={{
                fontFamily: "'Fraunces', Georgia, serif",
                fontStyle: 'italic',
                fontSize: 22,
                fontWeight: 500,
                color: '#42a57a',
              }}
            >
              ✓ {t.maxReached}
            </motion.p>
          )}
        </motion.div>

        {/* Filter Tabs */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex justify-center gap-3 mb-8"
        >
          {(['all', 'biotic', 'abiotic'] as const).map((key) => {
            const active = filter === key;
            const label = key === 'all' ? t.all : key === 'biotic' ? t.biotic : t.abiotic;
            return (
              <button
                key={key}
                onClick={() => setFilter(key)}
                style={{
                  padding: '10px 22px',
                  borderRadius: 999,
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: 18,
                  fontWeight: 600,
                  border: `1.5px solid ${active ? '#42a57a' : '#42a57a'}`,
                  background: active ? '#42a57a' : 'transparent',
                  color: active ? '#ffffff' : '#42a57a',
                  cursor: 'pointer',
                  transition: 'background-color 160ms ease, color 160ms ease',
                }}
              >
                {label}
              </button>
            );
          })}
        </motion.div>

        {/* Power Cards Carousel - Horizontal Scrolling */}
        <div className="relative mb-8 -mx-6 px-6">
          <div
            className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-4"
            style={{
              scrollPaddingLeft: '1.5rem',
              scrollSnapType: 'x mandatory',
              scrollbarWidth: 'none', // Firefox
              msOverflowStyle: 'none', // IE and Edge
            }}
          >
            <AnimatePresence mode="sync">
              {filteredPowers.map((power, index) => {
                const isSelected = isPowerSelected(power.id);
                const isMaxReached = selectedPowers.length >= MAX_POWERS && !isSelected;

                return (
                  <motion.button
                    key={power.id}
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{
                      duration: 0.3,
                      delay: index * 0.05
                    }}
                    whileHover={{ scale: isMaxReached ? 1 : 1.02, y: isMaxReached ? 0 : -8 }}
                    whileTap={{ scale: isMaxReached ? 1 : 0.98 }}
                    onClick={() => togglePower(power)}
                    className="relative flex-shrink-0 snap-start"
                    style={{
                      width: 'calc(22.5% - 1.125rem)',
                      minWidth: 224,
                      padding: 14,
                      borderRadius: 24,
                      background: isSelected ? '#42a57a' : '#ffffff',
                      color: isSelected ? '#ffffff' : '#2a6d5b',
                      border: isSelected
                        ? '1.5px solid #42a57a'
                        : isMaxReached
                          ? '1px solid #e0d8c0'
                          : '1px solid #e8e0c8',
                      opacity: isMaxReached && !isSelected ? 0.55 : 1,
                      cursor: isMaxReached && !isSelected ? 'not-allowed' : 'pointer',
                      transition: 'background 200ms ease, color 200ms ease, border-color 200ms ease',
                      textAlign: 'left',
                    }}
                    disabled={isMaxReached && !isSelected}
                  >
                    {/* Pastel inner tile holds the icon */}
                    <div
                      style={{
                        background: isSelected ? 'rgba(255,255,255,0.18)' : '#dcecda',
                        borderRadius: 18,
                        padding: '32px 16px 28px',
                        textAlign: 'center',
                        marginBottom: 16,
                      }}
                    >
                      <div style={{ fontSize: 96, lineHeight: 1 }}>{power.icon}</div>
                    </div>

                    {/* Selected check */}
                    {isSelected && (
                      <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        style={{
                          position: 'absolute',
                          top: 22,
                          right: 22,
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          background: '#ffffff',
                          color: '#42a57a',
                          fontFamily: "'Inter', system-ui, sans-serif",
                          fontSize: 22,
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        ✓
                      </motion.div>
                    )}

                    <h3
                      style={{
                        fontFamily: "'Fraunces', Georgia, serif",
                        fontStyle: 'italic',
                        fontSize: 30,
                        fontWeight: 600,
                        marginBottom: 8,
                        color: 'inherit',
                      }}
                    >
                      {power.name[language]}
                    </h3>

                    <p
                      style={{
                        fontFamily: "'Inter', system-ui, sans-serif",
                        fontSize: 17,
                        lineHeight: 1.45,
                        color: isSelected ? 'rgba(255,255,255,0.92)' : '#5f6e66',
                      }}
                    >
                      {power.description[language]}
                    </p>

                    <div
                      style={{
                        marginTop: 16,
                        display: 'inline-block',
                        padding: '6px 14px',
                        borderRadius: 999,
                        fontFamily: "'Inter', system-ui, sans-serif",
                        fontSize: 13,
                        fontWeight: 600,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        background: isSelected ? 'rgba(255,255,255,0.18)' : '#f4efde',
                        color: isSelected ? '#ffffff' : '#42a57a',
                      }}
                    >
                      {power.type === 'biotic' ? t.biotic : t.abiotic}
                    </div>
                  </motion.button>
                );
              })}
            </AnimatePresence>
          </div>
        </div>

        {/* Scroll Hint */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mb-6"
          style={{
            fontFamily: "'Inter', system-ui, sans-serif",
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: '#9a9a9a',
          }}
        >
          ← Swipe to see more powers →
        </motion.div>

        {/* Continue Button */}
        <div className="text-center">
          {selectedPowers.length === 0 ? (
            <p
              className="mb-4"
              style={{
                fontFamily: "'Fraunces', Georgia, serif",
                fontStyle: 'italic',
                fontSize: 22,
                color: '#9a9a9a',
              }}
            >
              {t.selectOne}
            </p>
          ) : (
            <motion.button
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleContinue}
              style={{
                padding: '16px 36px',
                background: '#42a57a',
                color: '#ffffff',
                fontFamily: "'Inter', system-ui, sans-serif",
                fontSize: 22,
                fontWeight: 600,
                borderRadius: 999,
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 6px 22px rgba(66,165,122,0.32)',
              }}
            >
              {t.continue} ({selectedPowers.length}) →
            </motion.button>
          )}
        </div>
      </div>
    </div>
  );
}
